import React from 'react';

import { BadgeProps, useMantineTheme } from '@mantine/core';
import { DroxyBadge } from 'components/Droxy/DroxyBadge';
import i18n from 'i18n';

export type StatusType = 'enabled' | 'disabled' | 'warning' | 'error' | 'pending';

interface DroxyStatusBadgeProps extends Omit<BadgeProps, 'children'> {
  status: StatusType;
  customLabel?: string;
}

interface StatusConfig {
  defaultLabel: string;
  borderColor: string;
  textColor: string;
  backgroundColor: string;
}

export function DroxyStatusBadge({ status, customLabel, ...badgeProps }: DroxyStatusBadgeProps) {
  const theme = useMantineTheme();

  const statusConfig: Record<StatusType, StatusConfig> = {
    enabled: {
      defaultLabel: i18n.t('APP.STATUS.ENABLED'),
      borderColor: theme.colors.green[4],
      textColor: theme.colors.green[7],
      backgroundColor: theme.colors.green[0]
    },
    disabled: {
      defaultLabel: i18n.t('APP.STATUS.DISABLED'),
      borderColor: theme.colors.gray[3],
      textColor: theme.colors.gray[7],
      backgroundColor: 'transparent'
    },
    warning: {
      defaultLabel: i18n.t('APP.STATUS.WARNING'),
      borderColor: theme.colors.yellow[4],
      textColor: theme.colors.yellow[7],
      backgroundColor: theme.colors.yellow[0]
    },
    error: {
      defaultLabel: i18n.t('APP.STATUS.ERROR'),
      borderColor: theme.colors.red[4],
      textColor: theme.colors.red[7],
      backgroundColor: theme.colors.red[0]
    },
    pending: {
      defaultLabel: i18n.t('APP.STATUS.PENDING'),
      borderColor: theme.colors.blue[4],
      textColor: theme.colors.blue[7],
      backgroundColor: theme.colors.blue[0]
    }
  };

  const config = statusConfig[status];

  return (
    <DroxyBadge
      sx={{
        borderColor: config.borderColor,
        color: config.textColor,
        backgroundColor: config.backgroundColor
      }}
      {...badgeProps}>
      {customLabel ?? config.defaultLabel}
    </DroxyBadge>
  );
}
